import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Chip,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { reservationsApi, roomsApi } from '../services/api';
import { format } from 'date-fns';
import { generateInvoice } from '../utils/invoiceGenerator';

interface ReservationDetailDialogProps {
  open: boolean;
  reservationId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ReservationDetail {
  id: number;
  guest: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  room: {
    id: number;
    number: string;
    type: string;
    rate: number;
  } | null;
  check_in_date: string;
  check_out_date: string;
  adults: number;
  children: number;
  status: string;
  total_amount: number;
  paid_amount: number;
  special_requests: string | null;
  notes: string | null;
  created_at: string;
}

const ReservationDetailDialog: React.FC<ReservationDetailDialogProps> = ({
  open,
  reservationId,
  onClose,
  onSuccess,
}) => {
  const [reservation, setReservation] = useState<ReservationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  const [checkInRoomId, setCheckInRoomId] = useState<string>('');
  const [checkOutPaidAmount, setCheckOutPaidAmount] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});

  useEffect(() => {
    if (open && reservationId) {
      fetchReservation();
      if (reservation?.status === 'confirmed' || reservation?.status === 'pending') {
        fetchAvailableRooms();
      }
    }
  }, [open, reservationId]);

  const fetchReservation = async () => {
    if (!reservationId) return;
    try {
      setLoading(true);
      const response = await reservationsApi.getById(reservationId);
      setReservation(response.data.data);
      setCheckOutPaidAmount(response.data.data.total_amount.toString());
      setEditData({
        check_in_date: response.data.data.check_in_date,
        check_out_date: response.data.data.check_out_date,
        adults: response.data.data.adults,
        children: response.data.data.children,
        special_requests: response.data.data.special_requests || '',
        notes: response.data.data.notes || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reservation');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRooms = async () => {
    if (!reservation) return;
    try {
      const response = await roomsApi.getAll();
      const rooms = response.data.data || [];
      // Filter available rooms
      const available = rooms.filter((room: any) => 
        room.status === 'available' || room.status === 'reserved'
      );
      setAvailableRooms(available);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    }
  };

  const handleCheckIn = async () => {
    if (!reservationId) return;
    
    if (!checkInRoomId && !reservation?.room?.id) {
      setError('Please select a room for check-in');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await reservationsApi.checkIn(reservationId, {
        room_id: parseInt(checkInRoomId) || reservation?.room?.id,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to check in');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    if (!reservationId) return;
    
    const paidAmount = parseFloat(checkOutPaidAmount);
    if (isNaN(paidAmount) || paidAmount < 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await reservationsApi.checkOut(reservationId, {
        paid_amount: paidAmount,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to check out');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!reservationId) return;
    
    if (!window.confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await reservationsApi.cancel(reservationId);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel reservation');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!reservationId) return;

    try {
      setProcessing(true);
      setError(null);
      await reservationsApi.update(reservationId, editData);
      setEditing(false);
      fetchReservation();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update reservation');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      pending: 'warning',
      confirmed: 'info',
      checked_in: 'success',
      checked_out: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const calculateNights = () => {
    if (!reservation) return 0;
    const checkIn = new Date(reservation.check_in_date);
    const checkOut = new Date(reservation.check_out_date);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleGenerateInvoice = () => {
    if (!reservation) return;

    const nights = calculateNights();
    const roomRate = reservation.room?.rate || 0;
    const subtotal = reservation.total_amount;
    const tax = 0; // Can be calculated if needed
    const serviceCharge = 0; // Can be calculated if needed
    const total = reservation.total_amount;

    generateInvoice({
      invoiceNumber: `INV-${reservation.id}-${Date.now()}`,
      reservationId: reservation.id,
      guest: {
        name: `${reservation.guest.firstName} ${reservation.guest.lastName}`,
        email: reservation.guest.email,
        phone: reservation.guest.phone,
      },
      room: reservation.room ? {
        number: reservation.room.number,
        type: reservation.room.type,
      } : undefined,
      checkIn: reservation.check_in_date,
      checkOut: reservation.check_out_date,
      nights,
      roomRate,
      subtotal,
      tax,
      serviceCharge,
      total,
      paid: reservation.paid_amount,
      outstanding: total - reservation.paid_amount,
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!reservation) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Reservation Details</Typography>
          <Chip
            label={reservation.status.replace('_', ' ').toUpperCase()}
            color={getStatusColor(reservation.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Guest Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Guest Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1">
                      {reservation.guest.firstName} {reservation.guest.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{reservation.guest.email}</Typography>
                  </Grid>
                  {reservation.guest.phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1">{reservation.guest.phone}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Reservation Details */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Reservation Details</Typography>
                  {!editing && (reservation.status === 'pending' || reservation.status === 'confirmed') && (
                    <IconButton size="small" onClick={() => setEditing(true)}>
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Check-in Date</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={editData.check_in_date ? format(new Date(editData.check_in_date), 'yyyy-MM-dd') : ''}
                        onChange={(e) => setEditData({ ...editData, check_in_date: e.target.value })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">
                        {format(new Date(reservation.check_in_date), 'MMM dd, yyyy')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Check-out Date</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={editData.check_out_date ? format(new Date(editData.check_out_date), 'yyyy-MM-dd') : ''}
                        onChange={(e) => setEditData({ ...editData, check_out_date: e.target.value })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">
                        {format(new Date(reservation.check_out_date), 'MMM dd, yyyy')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Room</Typography>
                    <Typography variant="body1">
                      {reservation.room ? `Room ${reservation.room.number} - ${reservation.room.type}` : 'Not Assigned'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Guests</Typography>
                    <Typography variant="body1">
                      {reservation.adults} Adult{reservation.adults !== 1 ? 's' : ''}
                      {reservation.children > 0 && `, ${reservation.children} Child${reservation.children !== 1 ? 'ren' : ''}`}
                    </Typography>
                  </Grid>
                  {reservation.special_requests && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Special Requests</Typography>
                      {editing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={editData.special_requests}
                          onChange={(e) => setEditData({ ...editData, special_requests: e.target.value })}
                          margin="dense"
                        />
                      ) : (
                        <Typography variant="body1">{reservation.special_requests}</Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Financial Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Nights</Typography>
                    <Typography variant="h6">{calculateNights()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                    <Typography variant="h6">${reservation.total_amount.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Paid Amount</Typography>
                    <Typography variant="h6" color={reservation.paid_amount > 0 ? 'success.main' : 'text.primary'}>
                      ${reservation.paid_amount.toFixed(2)}
                    </Typography>
                  </Grid>
                  {reservation.paid_amount < reservation.total_amount && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="error">
                        Outstanding: ${(reservation.total_amount - reservation.paid_amount).toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Check-in Section */}
          {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Check-In</Typography>
                  {!reservation.room && (
                    <TextField
                      fullWidth
                      select
                      label="Select Room"
                      value={checkInRoomId}
                      onChange={(e) => setCheckInRoomId(e.target.value)}
                      margin="normal"
                      required
                    >
                      <MenuItem value="">Select a room</MenuItem>
                      {availableRooms.map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          Room {room.room_number} - {room.room_type?.name || 'No Type'} 
                          {room.room_type?.base_rate && ` ($${room.room_type.base_rate}/night)`}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {reservation.room && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Room {reservation.room.number} is already assigned
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Check-out Section */}
          {reservation.status === 'checked_in' && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Check-Out</Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Payment Amount"
                    value={checkOutPaidAmount}
                    onChange={(e) => setCheckOutPaidAmount(e.target.value)}
                    margin="normal"
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText={`Total due: $${reservation.total_amount.toFixed(2)}`}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => { setEditing(false); setEditData({}); }} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} variant="contained" disabled={processing}>
              {processing ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleGenerateInvoice}
              variant="outlined"
              startIcon={<PdfIcon />}
            >
              Generate Invoice
            </Button>
            <Button onClick={onClose}>Close</Button>
            {reservation.status === 'checked_in' && (
              <Button
                onClick={handleCheckOut}
                variant="contained"
                color="success"
                startIcon={<CheckOutIcon />}
                disabled={processing}
              >
                {processing ? <CircularProgress size={24} /> : 'Check Out'}
              </Button>
            )}
            {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
              <>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  disabled={processing}
                >
                  Cancel Reservation
                </Button>
                <Button
                  onClick={handleCheckIn}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckInIcon />}
                  disabled={processing || (!checkInRoomId && !reservation.room)}
                >
                  {processing ? <CircularProgress size={24} /> : 'Check In'}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReservationDetailDialog;

